/**
 * ChannelRegistry.js
 * ------------------
 * Central ledger of every active Echo channel subscription.
 *
 * Problems this solves:
 *   1. Duplicate subscriptions (registering the same channel twice stacks listeners)
 *   2. Stale channels after navigation (no cleanup = ghost listeners)
 *   3. No visibility into what is currently subscribed
 *
 * API:
 *   registry.subscribe(name, setupFn)  – subscribe once, idempotent
 *   registry.unsubscribe(name)         – leave + remove
 *   registry.unsubscribeAll()          – full teardown (logout / org switch)
 *   registry.has(name)                 – check subscription exists
 */

import echo from "./EchoConnection";

class ChannelRegistry {
    /** @type {Map<string, object>} channelName → Echo channel instance */
    #channels = new Map();

    /**
     * Subscribe to a channel if not already subscribed.
     *
     * @param {string}   name     - Echo channel name, e.g. "workspace.42"
     * @param {(channel: object) => void} setupFn
     *   Called with the fresh channel so the caller can attach .listen() handlers.
     *   NOT called if the channel is already active.
     *
     * @returns {object} The Echo channel instance
     */
    subscribe(name, setupFn) {
        if (this.#channels.has(name)) {
            console.log(`[ChannelRegistry] Already subscribed: ${name}`);
            return this.#channels.get(name);
        }

        console.log(`[ChannelRegistry] Subscribing: ${name}`);
        const channel = echo.channel(name);
        this.#channels.set(name, channel);

        try {
            setupFn(channel);
        } catch (err) {
            console.error(`[ChannelRegistry] setupFn threw for "${name}":`, err);
            this.unsubscribe(name);
        }

        return channel;
    }

    /**
     * Subscribe to a private Echo channel (e.g. document.{type}.{id}).
     * Same idempotency rules as subscribe(); uses echo.private(name).
     *
     * @param {string}   name     - Echo private channel name without leading "private-"
     * @param {(channel: object) => void} setupFn
     * @returns {object} Echo channel instance
     */
    subscribePrivate(name, setupFn) {
        if (this.#channels.has(name)) {
            console.log(`[ChannelRegistry] Already subscribed (private): ${name}`);
            return this.#channels.get(name);
        }

        console.log(`[ChannelRegistry] Subscribing (private): ${name}`);
        const channel = echo.private(name);
        this.#channels.set(name, channel);

        try {
            setupFn(channel);
        } catch (err) {
            console.error(
                `[ChannelRegistry] setupFn threw for private "${name}":`,
                err,
            );
            this.unsubscribe(name);
        }

        return channel;
    }

    /**
     * Leave a channel and remove it from the registry.
     * Safe to call even if the channel was never subscribed.
     *
     * @param {string} name
     */
    unsubscribe(name) {
        if (!this.#channels.has(name)) return;

        console.log(`[ChannelRegistry] Unsubscribing: ${name}`);
        echo.leaveChannel(name);
        this.#channels.delete(name);
    }

    /**
     * Re-subscribe to a channel (force fresh listeners).
     * Useful after a reconnect if the channel may be stale.
     *
     * @param {string}   name
     * @param {Function} setupFn
     */
    resubscribe(name, setupFn) {
        this.unsubscribe(name);
        this.subscribe(name, setupFn);
    }

    /**
     * Leave every active channel. Call on logout or organization switch.
     */
    unsubscribeAll() {
        console.log(`[ChannelRegistry] Tearing down ${this.#channels.size} channel(s).`);
        for (const name of this.#channels.keys()) {
            echo.leaveChannel(name);
        }
        this.#channels.clear();
    }

    /** @param {string} name */
    has(name) {
        return this.#channels.has(name);
    }

    /** For debugging – list all active channel names */
    active() {
        return [...this.#channels.keys()];
    }
}

// Export a singleton so every part of the app shares the same registry
export const channelRegistry = new ChannelRegistry();
